import type {
  AysCodeCodingSystem,
  AysCodingSystemSpecification,
} from '@bindoc-projects/code-domain-lib';
import type { AysCodeSpectrumMatching } from '@bindoc-projects/code-spectrum-domain-lib';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { BdArrayUtil, BdEquals } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdCondition
} from '@bindoc-projects/condition-domain-lib';
import { CODE_SPECTRUM_CONDITION_ID } from '../common-plugins';
import type { FilterDetailedText } from '../filter-detailed-text';

export const FilterConditionCodeSpectrumAtPluginType: FilterConditionPluginSpecification<
  FilterConditionCodeSpectrumAtObject,
  FilterConditionCodeSpectrumAtDto,
  FilterConditionCodeSpectrumAtConditionJson
> = {
  type: 'codes-at',
  category: 'case',
  displayName: { de: 'Ziffer-Spektra' },
  sortingIndex: 8,
  occurrence: 'multiple',
  fromObject(
    obj: FilterConditionCodeSpectrumAtDto,
  ): FilterConditionPluginImpl<FilterConditionCodeSpectrumAtObject> {
    const codingSystem: AysCodeCodingSystem = obj.codingSystem || 'ICD-10-AT';
    const matching = obj.matching || 'exact';
    let codeTypeSpecification: AysCodingSystemSpecification;
    if (obj.codeTypeSpecification) {
      codeTypeSpecification = obj.codeTypeSpecification;
    } else if (codingSystem === 'ICD-10-AT') {
      codeTypeSpecification = 'mainDiagnosis';
    }

    return new FilterConditionCodeSpectrumAtPlugin(
        obj.codeSpectrumIds || [],
        codingSystem,
        codeTypeSpecification,
        matching,
        obj.includes,
      );
  },
  fromCondition(
    jsonObj: FilterConditionCodeSpectrumAtConditionJson,
  ): FilterConditionCodeSpectrumAtPlugin {
    return new FilterConditionCodeSpectrumAtPlugin(
        jsonObj.includes ?? jsonObj.excludes,
        jsonObj.codingSystem,
        jsonObj.codeTypeSpecification,
        jsonObj.matching,
        !(jsonObj.excludes && Array.isArray(jsonObj.excludes)),
      );
  },
  newInstance(): FilterConditionPluginImpl<FilterConditionCodeSpectrumAtObject> {
    return new FilterConditionCodeSpectrumAtPlugin([], 'ICD-10-AT');
  },
};

export class FilterConditionCodeSpectrumAtPlugin
  extends FilterConditionPluginImpl<
    FilterConditionCodeSpectrumAtObject,
    FilterConditionCodeSpectrumAtDto
  >
  implements FilterConditionCodeSpectrumAtObject
{
  static override Specification: FilterConditionPluginSpecification<
    FilterConditionCodeSpectrumAtObject,
    FilterConditionCodeSpectrumAtDto,
    FilterConditionCodeSpectrumAtConditionJson
  > = FilterConditionCodeSpectrumAtPluginType;

  constructor(
    public codeSpectrumIds: string[],
    public codingSystem: AysCodeCodingSystem,
    public codeTypeSpecification?: AysCodingSystemSpecification,
    public matching: AysCodeSpectrumMatching = 'exact',
    public includes = true,
  ) {
    super(FilterConditionCodeSpectrumAtPluginType.type);
  }

  clone(): FilterConditionCodeSpectrumAtPlugin {
    return new FilterConditionCodeSpectrumAtPlugin(
      [...this.codeSpectrumIds],
      this.codingSystem,
      this.codeTypeSpecification,
      this.matching,
      this.includes,
    );
  }

  override equals(other: FilterConditionCodeSpectrumAtPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.codeSpectrumIds, other.codeSpectrumIds) &&
      BdEquals.equals(this.codingSystem, other.codingSystem) &&
      BdEquals.equals(
        this.codeTypeSpecification,
        other.codeTypeSpecification,
      ) &&
      BdEquals.equals(this.matching, other.matching) &&
      BdEquals.equals(this.includes, other.includes)
    );
  }

  override getChildResources(): {
    [resourceName in ShareableType]?: string[];
  } {
    const codeSpectrumIds: string[] = [];

    this.codeSpectrumIds.forEach((codeSpectrumId) => {
      if (codeSpectrumIds.indexOf(codeSpectrumId) === -1) {
        codeSpectrumIds.push(codeSpectrumId);
      }
    });

    return { [ShareableType.CODE_SPECTRUM]: codeSpectrumIds };
  }

  override valid(): boolean {
    return (
      super.valid() && this.codeSpectrumIds && this.codeSpectrumIds.length > 0
    );
  }

  override validationToText(): string {
    if (this.codeSpectrumIds.length === 0) {
      return 'Mindestens ein Ziffer-Spektrum auswählen';
    } else {
      return '';
    }
  }

  override toDetailedText(resolvedNames?: Map<string, string>): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.codeSpectrumIds.map(
      (id) => resolvedNames?.get(id) ?? id,
    );
    return { title, items };
  }

  toText(): string | null {
    return this.codeSpectrumIds.length === 1
      ? 'ein Ziffer-Spektrum'
      : `${this.codeSpectrumIds.length} Ziffer-Spektra`;
  }

  toJSON(): FilterConditionCodeSpectrumAtDto | null {
    return {
      type: this.type,
      codingSystem: this.codingSystem,
      codeSpectrumIds: this.codeSpectrumIds,
      codeTypeSpecification: this.codeTypeSpecification,
      matching: this.matching,
      includes: this.includes,
    };
  }

  toValue(): FilterConditionCodeSpectrumAtObject {
    return this;
  }

  public toCondition(): BdCondition {
    return new FilterConditionCodeSpectrumAtCondition(this);
  }
}

export class FilterConditionCodeSpectrumAtCondition extends BdCondition {
  constructor(private _codeSpectrum: FilterConditionCodeSpectrumAtObject) {
    super(CODE_SPECTRUM_CONDITION_ID);
  }

  toJSON(): FilterConditionDto {
    return {
      [this.id]: {
        includes: this._codeSpectrum.includes
          ? this._codeSpectrum.codeSpectrumIds
          : undefined,
        excludes: this._codeSpectrum.includes
          ? undefined
          : this._codeSpectrum.codeSpectrumIds,
        codingSystem: this._codeSpectrum.codingSystem,
        matching: this._codeSpectrum.matching,
        codeTypeSpecification: this._codeSpectrum.codeTypeSpecification,
      },
    };
  }
}

interface FilterConditionCodeSpectrumAtObject {
  codeSpectrumIds: string[];
  codingSystem: AysCodeCodingSystem;
  codeTypeSpecification?: AysCodingSystemSpecification;
  matching: AysCodeSpectrumMatching;
  includes?: boolean;
}

interface FilterConditionCodeSpectrumAtDto
  extends FilterConditionCodeSpectrumAtObject {
  type: string;
}

interface FilterConditionCodeSpectrumAtConditionJson
  extends Omit<FilterConditionCodeSpectrumAtObject, 'includes'> {
  includes: string[] | undefined;
  excludes: string[] | undefined;
}
